import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  CardActions,
  Avatar,
  Chip,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Alert,
  useTheme,
  useMediaQuery,
  Tooltip,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Store as StoreIcon,
  LocalOffer as PackageIcon,
  BookOnline as BookingIcon,
  TrendingUp as AnalyticsIcon,
  PeopleAlt as CustomersIcon,
  AttachMoney as RevenueIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';
import { useVenue } from '../../contexts/VenueContext';
import { getVenueById, getPackagesByVenueId } from '../../services/venueService';
import BookingService, { PackageBooking } from '../../services/bookingService';
import { formatCurrency } from '../../utils/formatters';
import { Package, Venue } from '../../types/Venue';

const VenueDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { venueId } = useParams<{ venueId: string }>();
  const { setActiveVenue } = useVenue();

  const [venue, setVenue] = useState<Venue | null>(null);
  const [packages, setPackages] = useState<Package[]>([]);
  const [bookings, setBookings] = useState<PackageBooking[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Metrics
  const [totalRevenue, setTotalRevenue] = useState<number>(0);
  const [pendingBookings, setPendingBookings] = useState<number>(0);
  const [completedBookings, setCompletedBookings] = useState<number>(0);
  const [totalBookings, setTotalBookings] = useState<number>(0);
  const [activePackages, setActivePackages] = useState<number>(0);

  useEffect(() => {
    const fetchVenueData = async () => {
      if (!venueId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch venue details
        const venueData = await getVenueById(venueId);
        setVenue(venueData);
        setActiveVenue(venueId);
        
        // Fetch packages
        const packagesData = await getPackagesByVenueId(venueId);
        setPackages(packagesData);
        setActivePackages(packagesData.filter(pkg => pkg.isActive).length);
        
        // Fetch bookings
        const bookingsData = await BookingService.getVenueBookings(venueId);
        setBookings(bookingsData);
        
        // Calculate metrics
        setTotalBookings(bookingsData.length);
        setPendingBookings(bookingsData.filter((booking: PackageBooking) => booking.status === 'pending').length);
        setCompletedBookings(bookingsData.filter((booking: PackageBooking) => booking.status === 'completed').length);
        
        // Calculate total revenue
        const revenue = bookingsData
          .filter((booking: PackageBooking) => booking.status === 'completed' || booking.status === 'confirmed')
          .reduce((total: number, booking: PackageBooking) => total + (booking.amount || 0), 0);
        setTotalRevenue(revenue);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching venue data:', err);
        setError('Failed to load venue data. Please try again.');
        setLoading(false);
      }
    };
    
    fetchVenueData();
  }, [venueId, setActiveVenue]);

  const handleCreatePackage = () => {
    navigate(`/venues/${venueId}/packages/create`);
  };

  const handleViewPackage = (packageId: string) => {
    navigate(`/venues/${venueId}/packages/${packageId}`);
  };

  const handleEditPackage = (packageId: string) => {
    navigate(`/venues/${venueId}/packages/${packageId}/edit`);
  };

  const handleViewBookings = () => {
    navigate(`/venues/${venueId}/bookings`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return theme.palette.success.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'cancelled':
        return theme.palette.error.main;
      case 'completed':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Loading venue dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!venue) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Venue not found. Please check the URL or create a new venue.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {venue.name} Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your packages, bookings, and venue performance
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreatePackage}
        >
          Create Package
        </Button>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 140,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Total Revenue
            </Typography>
            <Typography variant="h4" component="div" sx={{ my: 1 }}>
              {formatCurrency(totalRevenue)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <RevenueIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2">
                From {totalBookings} bookings
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 140,
              borderLeft: `4px solid ${theme.palette.success.main}`,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Active Packages
            </Typography>
            <Typography variant="h4" component="div" sx={{ my: 1 }}>
              {activePackages}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PackageIcon sx={{ color: theme.palette.success.main, mr: 1 }} />
              <Typography variant="body2">
                {packages.length > activePackages 
                  ? `${packages.length - activePackages} inactive` 
                  : 'All packages active'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 140,
              borderLeft: `4px solid ${theme.palette.warning.main}`,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Pending Bookings
            </Typography>
            <Typography variant="h4" component="div" sx={{ my: 1 }}>
              {pendingBookings}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BookingIcon sx={{ color: theme.palette.warning.main, mr: 1 }} />
              <Typography variant="body2">
                Need your attention
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 140,
              borderLeft: `4px solid ${theme.palette.info.main}`,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Completed Bookings
            </Typography>
            <Typography variant="h4" component="div" sx={{ my: 1 }}>
              {completedBookings}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CustomersIcon sx={{ color: theme.palette.info.main, mr: 1 }} />
              <Typography variant="body2">
                {totalBookings > 0 
                  ? `${Math.round((completedBookings / totalBookings) * 100)}% completion rate`
                  : 'No bookings yet'}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Packages and Bookings */}
      <Grid container spacing={3}>
        {/* Packages Section */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" component="h2">
                Your Packages
              </Typography>
              <Button
                size="small"
                onClick={handleCreatePackage}
                startIcon={<AddIcon />}
              >
                Add Package
              </Button>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            {packages.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PackageIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  You don't have any packages yet
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Create your first package to start receiving bookings
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreatePackage}
                  sx={{ mt: 2 }}
                >
                  Create First Package
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {packages.slice(0, 4).map((pkg) => (
                  <Grid item xs={12} sm={6} key={pkg.id}>
                    <Card elevation={1}>
                      <CardActionArea onClick={() => handleViewPackage(pkg.id)}>
                        <CardMedia
                          component="img"
                          height="140"
                          image={pkg.images[0] || 'https://via.placeholder.com/300x140?text=No+Image'}
                          alt={pkg.name}
                        />
                        <CardContent sx={{ pb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>
                              {pkg.name}
                            </Typography>
                            <Chip
                              label={pkg.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={pkg.isActive ? 'success' : 'default'}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {formatCurrency(pkg.price)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {pkg.description.substring(0, 60)}...
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 1.5 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewPackage(pkg.id)}
                            color="primary"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Package">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditPackage(pkg.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Share Package">
                          <IconButton size="small">
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            
            {packages.length > 4 && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  onClick={() => navigate(`/venues/${venueId}/packages`)}
                >
                  View All Packages
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Bookings Section */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" component="h2">
                Recent Bookings
              </Typography>
              <Button
                size="small"
                onClick={handleViewBookings}
              >
                View All
              </Button>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            {bookings.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <BookingIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  No bookings yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bookings will appear here once customers purchase your packages
                </Typography>
              </Box>
            ) : (
              <List sx={{ width: '100%' }}>
                {bookings.slice(0, 5).map((booking) => (
                  <React.Fragment key={booking.id}>
                    <ListItem 
                      alignItems="flex-start"
                      sx={{ px: 1, cursor: 'pointer' }}
                      onClick={() => navigate(`/venues/${venueId}/bookings/${booking.id}`)}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: getStatusColor(booking.status) }}>
                          {booking.userName?.substring(0, 1) || "G"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2">
                              {booking.userName || "Guest"}
                            </Typography>
                            <Typography variant="body2">
                              {formatCurrency(booking.amount || 0)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="span" color="text.primary">
                              {booking.packageName}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {booking.bookingDate?.toDate().toLocaleDateString() || "No date"}
                              </Typography>
                              <Chip
                                label={booking.status}
                                size="small"
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.7rem',
                                  bgcolor: getStatusColor(booking.status),
                                  color: 'white'
                                }}
                              />
                            </Box>
                          </>
                        }
                      />
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Stats Section */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Venue Information
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <StoreIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="subtitle2">Venue Type</Typography>
                <Typography variant="body1">
                  {venue.type.replace('_', ' ').charAt(0).toUpperCase() + venue.type.replace('_', ' ').slice(1)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <CalendarIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="subtitle2">Business Hours</Typography>
                <Typography variant="body1">
                  {venue.businessHours.length > 0 
                    ? `${venue.businessHours.length} days configured` 
                    : 'No business hours set'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <CustomersIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="subtitle2">Contact</Typography>
                <Typography variant="body1">
                  {venue.contact.email}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Detailed Venue Information */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Detailed Venue Information
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              Basic Information
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Name:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">{venue.name}</Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Description:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">{venue.description || 'No description provided'}</Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Created:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">
                    {venue.createdAt ? new Date(venue.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider', pb: 1, mt: 3 }}>
              Amenities
            </Typography>
            
            {venue.amenities && venue.amenities.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {venue.amenities.map((amenity, index) => (
                  <Chip 
                    key={index} 
                    label={amenity} 
                    size="small" 
                    sx={{ mb: 1 }} 
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No amenities listed</Typography>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              Location Information
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Address:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">{venue.location.address}</Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">City:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">{venue.location.city}</Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">State:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">{venue.location.state}</Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Postal Code:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">{venue.location.postalCode}</Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Country:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">{venue.location.country}</Typography>
                </Grid>
              </Grid>
            </Box>
            
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider', pb: 1, mt: 3 }}>
              Contact Information
            </Typography>
            
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">Email:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body2">{venue.contact.email}</Typography>
              </Grid>
              
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">Phone:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body2">{venue.contact.phone}</Typography>
              </Grid>
              
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">Website:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body2">
                  {venue.contact.website ? (
                    <Link href={venue.contact.website} target="_blank" rel="noopener noreferrer">
                      {venue.contact.website}
                    </Link>
                  ) : (
                    'Not provided'
                  )}
                </Typography>
              </Grid>
            </Grid>

            {venue.socialMedia && Object.values(venue.socialMedia).some(value => value) && (
              <>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider', pb: 1, mt: 3 }}>
                  Social Media
                </Typography>
                
                <Grid container spacing={1}>
                  {venue.socialMedia.facebook && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Facebook:</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Link href={venue.socialMedia.facebook} target="_blank" rel="noopener noreferrer">
                          <Typography variant="body2">View Facebook Page</Typography>
                        </Link>
                      </Grid>
                    </>
                  )}
                  
                  {venue.socialMedia.instagram && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Instagram:</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Link href={venue.socialMedia.instagram} target="_blank" rel="noopener noreferrer">
                          <Typography variant="body2">View Instagram Profile</Typography>
                        </Link>
                      </Grid>
                    </>
                  )}
                  
                  {venue.socialMedia.twitter && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Twitter:</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Link href={venue.socialMedia.twitter} target="_blank" rel="noopener noreferrer">
                          <Typography variant="body2">View Twitter Profile</Typography>
                        </Link>
                      </Grid>
                    </>
                  )}
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
        
        {/* Business Hours Section */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              Business Hours
            </Typography>
            
            {venue.businessHours && venue.businessHours.length > 0 ? (
              <Grid container spacing={2}>
                {venue.businessHours.map((hours, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', textAlign: 'center' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][hours.dayOfWeek]}
                      </Typography>
                      
                      {hours.isClosed ? (
                        <Typography variant="body2" color="text.secondary">Closed</Typography>
                      ) : (
                        <Typography variant="body2">
                          {hours.open} - {hours.close}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">No business hours have been set for this venue.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VenueDashboard;
